import { useApi } from "@/hooks";
import PositionConfig from "@Constants/game/position-config";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PhaseOfPlayMap from "@Enums/PhaseOfPlayMap";
import PlayPositions from "@Enums/PlayPositions";
import TaggableTypes from "@Enums/TaggableTypes";
import useI18n from "@i18n";
import { CircularProgress, Stack } from "@mui/material";
import { Api } from "@Shared/schemas";
import { TagRecord } from "@Shared/schemas/tags/tagSchemas";
import { useEffect, useState } from "react";
import { DialogContentDivider, FilterDialog, FilterDialogSection } from "../common";
import { handleMultiSelectStateChange, handleStateChange } from "../common/filter/helpers";
import { FilterItem } from "../common/filter/types";

/**
 * This component is part of the VR app V2 redesign
 */

export type FilterParam = "published" | "play_positions" | "phase_of_play" | "tags";
export type Filter = {
  [K in FilterParam]?: boolean | PhaseOfPlay | PlayPositions[] | string[] | undefined;
};

interface IScenarioFiltersDialog {
  open: boolean;
  // onApply: (filter: (scenario: ScenarioResponse) => boolean) => void;
  onApply: (filter: Filter | undefined) => void;
  onClose: () => void;
}

const ScenarioFiltersDialog = ({ open, onApply, onClose }: IScenarioFiltersDialog) => {
  const { i18n } = useI18n();
  const [filters, setFilters] = useState<Filter>();

  const [states, setStates] = useState<FilterItem[]>([
    { label: "All", value: "all", selected: true },
    { label: "Published", value: true, selected: false },
    { label: "Draft", value: false, selected: false },
  ]);

  const [phases, setPhases] = useState<FilterItem[]>([
    { label: "All", value: "all", selected: true },
    { label: PhaseOfPlayMap[PhaseOfPlay.COUNTER_ATTACK], value: PhaseOfPlay.COUNTER_ATTACK, selected: false },
    { label: PhaseOfPlayMap[PhaseOfPlay.BUILD_UP], value: PhaseOfPlay.BUILD_UP, selected: false },
  ]);

  const [positions, setPositions] = useState<FilterItem[]>([
    {
      label: "All",
      value: "all",
      selected: true,
    },
    ...Object.values(PositionConfig)
      .flat()
      .map((position) => ({
        label: i18n(position.fullnameKey),
        value: position.id,
        selected: false,
      })),
  ]);

  const [tags, setTags] = useState<FilterItem[]>([{ label: "All", value: "all", selected: true }]);

  const {
    call: requestTags,
    data: requestTagsResponse,
    loading: requestTagsLoading,
  } = useApi<typeof Api.Tags.RequestTags>({
    api: Api.Tags.RequestTags,
    request: {
      taggable_type: TaggableTypes.SCENARIOS,
    },
  });

  useEffect(() => {
    requestTags();
  }, []);

  useEffect(() => {
    if (requestTagsResponse) {
      const newTags = [
        { label: "All", value: "all", selected: true },
        ...requestTagsResponse.map((tag: TagRecord) => ({ label: tag.tag, value: tag.tag })),
      ];

      setTags(newTags);
    } else {
      setTags([{ label: "All", value: "all", selected: true }]);
    }
  }, [requestTagsResponse]);

  const handleStateClick = (index: number) => {
    handleStateChange(setStates, index);
    const state = states[index];
    setFilters((prev) => ({ ...prev, published: state.value === "all" ? undefined : state.value }));
  };

  const handlePhaseClick = (index: number) => {
    handleStateChange(setPhases, index);
    const phase = phases[index];
    setFilters((prev) => ({ ...prev, phase_of_play: phase.value === "all" ? undefined : phase.value }));
  };

  const handlePositionClick = (index: number, prevSelected: boolean) => {
    const position = positions[index];
    handleMultiSelectStateChange(setPositions, index, prevSelected, position.value === "all");
    const newPosition = position.value;
    let prevPositions = (filters?.play_positions as PlayPositions[]) || [];
    if (prevSelected) {
      prevPositions = prevPositions.filter((prev) => prev !== newPosition);
    } else {
      prevPositions = [...prevPositions, newPosition];
    }
    setFilters((prev) => ({ ...prev, play_positions: newPosition === "all" ? undefined : prevPositions }));
  };

  const handleTagClick = (index: number, prevSelected: boolean) => {
    const tag = tags[index];
    handleMultiSelectStateChange(setTags, index, prevSelected, tag.value === "all");
    const newTag = tag.value;
    let prevTags = (filters?.tags as string[]) || [];
    if (prevSelected) {
      prevTags = prevTags.filter((prev) => prev !== newTag);
    } else {
      prevTags = [...prevTags, newTag];
    }
    setFilters((prev) => ({ ...prev, tags: newTag === "all" ? undefined : prevTags }));
  };

  return (
    <FilterDialog
      open={open}
      onApply={() => {
        onApply(filters);
        onClose();
      }}
      onClose={onClose}
    >
      <Stack spacing={5}>
        <FilterDialogSection title="State" items={states} onClick={(index: number) => handleStateClick(index)} />
        <DialogContentDivider />
        <FilterDialogSection title="Phase of play" items={phases} onClick={(index: number) => handlePhaseClick(index)} />
        <DialogContentDivider />
        <FilterDialogSection title="Play positions" items={positions} onClick={(index, selected) => handlePositionClick(index, selected || false)} />
        <DialogContentDivider />
        {requestTagsLoading ? (
          <CircularProgress />
        ) : (
          <FilterDialogSection title="Tags" items={tags} onClick={(index, selected) => handleTagClick(index, selected || false)} />
        )}
      </Stack>
    </FilterDialog>
  );
};

export default ScenarioFiltersDialog;
